import { useMemo, useState, useEffect } from 'react';
import { useWorkOrderDetails } from '@api/enterprise/workorder';
import { OrderDetailStatus } from '@api/enterprise/openapi/models/order-detail-status';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { db } from '@db/database';
import type { WorkOrderDetailViewModel } from '@api/enterprise/openapi/models/work-order-detail-view-model';

type SidebarEquipment = {
  sequenceNumber: number;
  globalId: string;
  id: string;
  description: string;
  status: OrderDetailStatus | undefined;
  isComplete: boolean;
};

export const useSidebarEquipment = (jobNumber: string) => {
  const isOnline = useOnlineStatus();
  const { data, isLoading, error } = useWorkOrderDetails(
    jobNumber,
    !!jobNumber
  );
  const [offlineData, setOfflineData] = useState<
    WorkOrderDetailViewModel[] | null
  >(null);

  useEffect(() => {
    if (!isOnline && !data && error && jobNumber) {
      db.cachedWorkOrderDetails
        .where('jobNumber')
        .equals(String(jobNumber))
        .toArray()
        .then(cached => {
          if (cached.length > 0) {
            setOfflineData(
              cached.map(item => {
                const { originalId, ...rest } = item;
                return {
                  ...rest,
                  id: originalId
                } as WorkOrderDetailViewModel;
              })
            );
          }
        });
    }
  }, [isOnline, data, error, jobNumber]);

  const source = data || offlineData;

  const equipment = useMemo<SidebarEquipment[]>(() => {
    if (!source) return [];

    return source.map((detail: WorkOrderDetailViewModel) => {
      const isComplete =
        detail.status === OrderDetailStatus.Complete ||
        detail.status === OrderDetailStatus.Skipped;

      return {
        sequenceNumber: detail.sequenceNumber ?? 0,
        globalId: detail.globalId || '',
        id: detail.id || '',
        description: detail.description || '',
        status: detail.status,
        isComplete
      };
    });
  }, [source]);

  return {
    equipment,
    isLoading: isLoading && !offlineData,
    error: offlineData ? null : error
  };
};
